import * as React from "react";
import { offeringFamily, parseMicros, uniqueOfferings, type OfferingLike } from "@nodus/ui";
import { supportsCompute, workspaceOfferingInStock, workspaceOfferingLaunchable } from "./compute-selection";
import { memoryLabel } from "./gpu-availability";
import { quantityBytes } from "../storage/model";
import { spotLocationName } from "../spot/locations";
import { accessLabel, spotQuote } from "./spot-hardware";

export const regionName = (region?: string) => ({ us: "US", eu: "Europe", ca: "Canada", uk: "UK", apac: "Asia Pacific", unknown: "Unspecified region", any: "Any region" })[region ?? ""] ?? region ?? "Unspecified region";
export const hardwareRegionName = (o: OfferingLike) => { const quote = spotQuote(o); return quote ? spotLocationName(quote.region, quote.region_name) : regionName(o.spec?.regionClass); };
export const hardwareRegion = (o: OfferingLike) => o.spec?.regionClass ?? "unknown";
export const hardwareModelKey = (o: OfferingLike) => o.spec?.category === "CPU" ? `CPU:${o.spec.host?.cpu ?? "unknown"}` : `GPU:${offeringFamily(o)}`;
export const hardwareVariant = (o: OfferingLike) => JSON.stringify([o.spec?.accelerator?.id ?? offeringFamily(o), quantityBytes(o.spec?.accelerator?.memory) ?? o.spec?.accelerator?.memory]);
export const hardwareHost = (o: OfferingLike) => JSON.stringify([accessLabel(o), o.spec?.host ?? {}]);
const rate = (o: OfferingLike) => parseMicros(o.status?.priceUSDPerHour?.from) ?? Infinity;

/** Retain option context after removal; only the current catalog selection can authorize launch. */
export function useHardwareSelection(items: readonly OfferingLike[], name?: string) {
  const selected = items.find((o) => o.metadata.name === name);
  const [last, setLast] = React.useState<OfferingLike>();
  React.useEffect(() => { if (selected) setLast(selected); }, [selected]);
  return { selected, context: selected ?? (last?.metadata.name === name ? last : undefined) };
}

export function workspaceHardware(items: readonly OfferingLike[]): OfferingLike[] {
  return uniqueOfferings(items).filter((o) => supportsCompute(o, "workspaces") && (o.spec?.category === "CPU" || [1, 2, 4, 8].includes(o.spec?.count ?? 1)));
}

/** Keep compatible choices when changing one option; every result is an exact available offering. */
export function preferredHardware(items: readonly OfferingLike[], selected?: OfferingLike): OfferingLike | undefined {
  const preference = (o: OfferingLike) => selected ?
    Number(hardwareVariant(o) !== hardwareVariant(selected)) * 16 +
    Number((o.spec?.count ?? 1) !== (selected.spec?.count ?? 1)) * 8 +
    Number(Boolean(o.spec?.interruptible) !== Boolean(selected.spec?.interruptible)) * 4 +
    Number(hardwareHost(o) !== hardwareHost(selected)) * 2 : (o.spec?.count ?? 1) * 2 + Number(Boolean(o.spec?.interruptible));
  return [...items].sort((a, b) => Number(workspaceOfferingInStock(b)) - Number(workspaceOfferingInStock(a)) ||
    Number(workspaceOfferingLaunchable(b)) - Number(workspaceOfferingLaunchable(a)) ||
    Number(b.metadata.name === selected?.metadata.name) - Number(a.metadata.name === selected?.metadata.name) ||
    preference(a) - preference(b) || rate(a) - rate(b) || a.metadata.name.localeCompare(b.metadata.name))[0];
}

export function hardwareModels(items: readonly OfferingLike[], selected?: OfferingLike) {
  const groups = new Map<string, OfferingLike[]>();
  for (const o of workspaceHardware(items)) {
    const key = hardwareModelKey(o);
    groups.set(key, [...(groups.get(key) ?? []), o]);
  }
  return [...groups.entries()].map(([key, members]) => {
    const available = members.filter(workspaceOfferingLaunchable);
    const offering = preferredHardware(members, selected && hardwareModelKey(selected) === key ? selected : undefined)!;
    const cheapest = [...(available.length ? available : members)].sort((a, b) => rate(a) - rate(b))[0]!;
    const cpu = offering.spec?.category === "CPU";
    const memories = [...new Set(members.map((o) => cpu ? o.spec?.host?.memory : o.spec?.accelerator?.memory).filter((m): m is string => Boolean(m)))].sort((a, b) => (quantityBytes(a) ?? 0) - (quantityBytes(b) ?? 0));
    const memory = memories.length > 1 ? `${memoryLabel(memories[0])} – ${memoryLabel(memories[memories.length - 1])}` : memoryLabel(memories[0]) ?? "—";
    return { key, family: cpu ? "CPU" : offeringFamily(offering), name: cpu ? `${offering.spec?.host?.cpu ?? "—"} vCPU` : offeringFamily(offering), count: cpu ? 0 : 1, offering, memory, inStock: members.some(workspaceOfferingInStock), canLaunch: available.length > 0, from: cheapest.status?.priceUSDPerHour?.from };
  }).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
}
